import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchApi } from '@/common/lib/fetchApi'

export interface FusionAuthProfile {
  available: boolean
  reason: string | null
  user_id: string | null
  email: string | null
  verified: boolean | null
  active: boolean | null
  last_login_at: string | null
  roles: string[]
  raw: Record<string, unknown> | null
}

export interface EmployeeDetail {
  id: number
  first_name: string
  last_name: string
  email: string | null
  department: string | null
  job_title: string | null
  employment_status: string
  employment_history_status: string | null
  on_leave: boolean
  hire_date: string | null
  manager_id: number | null
  manager_name: string | null
  bamboo_employee_id: number | null
  auth_user_id: string | null
  timesheet_exempt: boolean
  work_days: number[]
  standard_daily_hours: number
  created_at: string
  updated_at: string
  fusionauth: FusionAuthProfile
}

export interface EmployeeTimesheetItem {
  timesheet_id: number
  pay_period_id: number
  pay_period_start: string
  pay_period_end: string
  status: string
  total_hours: number
  standard_hours: number
  straight_overtime_hours: number
  flsa_overtime_hours: number
  submitted_at: string | null
  approved_at: string | null
}

export interface SentNotificationItem {
  id: number
  event_type: string
  channel: string
  status: string
  sent_at: string
  pay_period_id: number | null
  error_message: string | null
}

export interface InAppNotificationItem {
  id: number
  type: string
  title: string
  message: string
  read: boolean
  created_at: string
}

export interface EmployeeNotifications {
  sent: SentNotificationItem[]
  in_app: InAppNotificationItem[]
}

export function useEmployeeDetail(id: number) {
  return useQuery({
    queryKey: ['admin', 'employee', id],
    queryFn: () => fetchApi<EmployeeDetail>(`/api/org/employees/${id}`),
  })
}

export function useUpdateEmployeeSchedule(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { work_days: number[]; standard_daily_hours: number }) =>
      fetchApi<EmployeeDetail>(`/api/org/employees/${id}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employee', id] })
    },
  })
}

export function useUpdateEmployeeExemption(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { timesheet_exempt: boolean }) =>
      fetchApi<EmployeeDetail>(`/api/org/employees/${id}/timesheet-exemption`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'employee', id] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'employees'] })
    },
  })
}

export function useEmployeeTimesheets(id: number) {
  return useQuery({
    queryKey: ['admin', 'employee', id, 'timesheets'],
    queryFn: () => fetchApi<EmployeeTimesheetItem[]>(`/api/org/employees/${id}/timesheets`),
  })
}

export function useEmployeeNotifications(id: number) {
  return useQuery({
    queryKey: ['admin', 'employee', id, 'notifications'],
    queryFn: () => fetchApi<EmployeeNotifications>(`/api/org/employees/${id}/notifications`),
  })
}
